import {createAsyncThunk} from "@reduxjs/toolkit";
import axios from "../common/AuthInterceptor";
import authHeader from "./auth-header";
import {setMessage} from "../slices/message";


const ADMIN_API_URL = process.env.REACT_APP_API_URL + "/admin/";
const CLIENT_API_URL = process.env.REACT_APP_API_URL + "/api/client/";

export const createUser = createAsyncThunk(
    "user/create",
    async ({ firstName, lastName, email, country }, thunkAPI) => {
        try {
            const response = await axios.post(ADMIN_API_URL + "createUser",
                {firstName, lastName, email, country}, { headers: authHeader() });
            thunkAPI.dispatch(setMessage(response.data));
            return response.data;
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data) ||
                error.message ||
                error.toString();
            console.log(error);
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    }
);

export const resetUserPassword = createAsyncThunk(
    "user/resetPwd",
    async ({ email, newPassword }, thunkAPI) => {
        try {
            const response = await axios.post(ADMIN_API_URL + "resetUserPassword",
                {email, newPassword}, { headers: authHeader() });
            thunkAPI.dispatch(setMessage(response.data));
            return response.data;
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data) ||
                error.message ||
                error.toString();
            console.log(error);
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    }
);

export const getClients = () => axios.get(ADMIN_API_URL + "getClients", { headers: authHeader() });

export const getDiscountSub = () => axios.get(ADMIN_API_URL + "getDiscountSub", { headers: authHeader() });

export const updateDiscount = (clientId, subId, discount) => axios.post(ADMIN_API_URL + "setDiscount", {clientId, subId, discount}, {headers: authHeader()});

export const uploadMessagePage = (hour, minute, second, messageTitle, messageAction, targetUrl, messageFile, detailFile, startDate, endDate, repeatIntervalDays, normalMessageFile) => {
    const formData = new FormData();
    formData.append("hour", hour);
    formData.append("minute", minute);
    formData.append("second", second);
    formData.append("messageTitle", messageTitle);
    formData.append("messageAction", messageAction);
    formData.append("targetUrl", targetUrl || "");
    formData.append("messagePage", messageFile);
    formData.append("detailPage", detailFile);

    if (startDate) {
        // Format date as YYYY-MM-DD
        const startDateFormatted = startDate.toISOString().split('T')[0];
        formData.append("startDate", startDateFormatted);
    }

    if (endDate) {
        // Format date as YYYY-MM-DD
        const endDateFormatted = endDate.toISOString().split('T')[0];
        formData.append("endDate", endDateFormatted);
    }

    if (repeatIntervalDays !== undefined && repeatIntervalDays !== null && repeatIntervalDays !== "") {
        formData.append("repeatIntervalDays", repeatIntervalDays);
    }

    if (normalMessageFile) {
        formData.append("normalMessagePage", normalMessageFile);
    }

    return axios.post(CLIENT_API_URL + "message/uploadMessagePage", formData, {
        headers: {
            ...authHeader(),
            "Content-Type": "multipart/form-data"
        }
    });
};

export const getClientGroups = () => axios.get(ADMIN_API_URL + "client-groups", { headers: authHeader() });
export const createClientGroup = (name, description, typeIds) => axios.post(ADMIN_API_URL + `client-groups?name=${name}&description=${description}&typeIds=${typeIds.join(',')}`, {}, { headers: authHeader() });
export const updateClientGroup = (groupId, name, description, typeIds) => axios.put(ADMIN_API_URL + `client-groups/${groupId}?name=${name}&description=${description}&typeIds=${typeIds.join(',')}`, {}, { headers: authHeader() });
export const deleteClientGroup = (id) => axios.delete(ADMIN_API_URL + "client-groups/" + id, { headers: authHeader() });
export const getGroupMembers = (id) => axios.get(ADMIN_API_URL + "client-groups/" + id + "/members", { headers: authHeader() });
export const addGroupMember = (groupId, clientId) => axios.post(ADMIN_API_URL + "client-groups/" + groupId + "/members/" + clientId, {}, { headers: authHeader() });
export const removeGroupMember = (groupId, clientId) => axios.delete(ADMIN_API_URL + "client-groups/" + groupId + "/members/" + clientId, { headers: authHeader() });
export const getAllGroupTypes = () => axios.get(ADMIN_API_URL + "group-types", { headers: authHeader() });
export const getGroupTypes = (groupId) => axios.get(ADMIN_API_URL + "group-types/" + groupId, { headers: authHeader() });
export const searchClients = (query) => axios.get(ADMIN_API_URL + "clients/search?query=" + query, { headers: authHeader() });
export const getAiAgents = () => axios.get(ADMIN_API_URL + "ai-agents", { headers: authHeader() });
export const getAiAgent = (id) => axios.get(ADMIN_API_URL + "ai-agents/" + id, { headers: authHeader() });
export const getAiAgentModels = () => axios.get(ADMIN_API_URL + "ai-agents/models", { headers: authHeader() });
export const getAiAgentVectorStores = () => axios.get(ADMIN_API_URL + "ai-agents/vector-stores", { headers: authHeader() });
export const createAiAgent = (payload) => axios.post(ADMIN_API_URL + "ai-agents", payload, { headers: authHeader() });
export const updateAiAgent = (id, payload) => axios.put(ADMIN_API_URL + "ai-agents/" + id, payload, { headers: authHeader() });
export const getClientSubscriptions = (clientId) => axios.get(ADMIN_API_URL + "client-subscriptions/" + encodeURIComponent(clientId), { headers: authHeader() });
export const getAvailableClientSubscriptions = (clientId) => axios.get(ADMIN_API_URL + "client-subscriptions/available?clientId=" + encodeURIComponent(clientId), { headers: authHeader() });
export const createClientSubscription = (payload) => axios.post(ADMIN_API_URL + "client-subscriptions", payload, { headers: authHeader() });
export const deleteClientSubscriptions = (ids) => axios.delete(ADMIN_API_URL + "client-subscriptions?ids=" + ids.join(','), { headers: authHeader() });
export const updateClientSubscriptionDates = (id, dates) => axios.put(ADMIN_API_URL + "client-subscriptions/" + id + "/dates", dates, { headers: authHeader() });
export const getSubscriptionClients = (subscriptionId, page = 0, size = 50, search = "") => axios.get(ADMIN_API_URL + `subscriptions/${subscriptionId}/clients?page=${page}&size=${size}&search=${encodeURIComponent(search)}`, { headers: authHeader() });

const adminService = {
    createUser,
    getClients,
    getDiscountSub,
    updateDiscount,
    uploadMessagePage,
    getClientGroups,
    createClientGroup,
    updateClientGroup,
    deleteClientGroup,
    getGroupMembers,
    addGroupMember,
    removeGroupMember,
    getAllGroupTypes,
    getGroupTypes,
    searchClients,
    getAiAgents,
    getAiAgent,
    getAiAgentModels,
    getAiAgentVectorStores,
    createAiAgent,
    updateAiAgent,
    getClientSubscriptions,
    getAvailableClientSubscriptions,
    createClientSubscription,
    deleteClientSubscriptions,
    updateClientSubscriptionDates,
    getSubscriptionClients,
    getUsageSummary: (type, period, clientName = '', page = 0, size = 50) => axios.get(ADMIN_API_URL + `usage-summary?type=${type}&period=${period || ''}&clientName=${clientName}&page=${page}&size=${size}`, { headers: authHeader() }),
    getUsageSummaryPeriods: (type) => axios.get(ADMIN_API_URL + "usage-summary/periods?type=" + type, { headers: authHeader() }),
    getAllNotifications: () => axios.get(ADMIN_API_URL + "notifications", { headers: authHeader() }),
    getGroupNotifications: (groupId) => axios.get(ADMIN_API_URL + "group-notifications/" + groupId, { headers: authHeader() }),
    addGroupNotification: (groupId, notificationId, email) => axios.post(ADMIN_API_URL + `group-notifications?groupId=${groupId}&notificationId=${notificationId}&email=${encodeURIComponent(email || '')}`, {}, { headers: authHeader() }),
    removeGroupNotification: (groupId, notificationId) => axios.delete(ADMIN_API_URL + `group-notifications?groupId=${groupId}&notificationId=${notificationId}`, { headers: authHeader() }),
    getClientNotifications: (clientId) => axios.get(ADMIN_API_URL + "client-notifications/" + clientId, { headers: authHeader() }),
    addClientNotification: (clientId, notificationId, email) => axios.post(ADMIN_API_URL + `client-notifications?clientId=${clientId}&notificationId=${notificationId}&email=${encodeURIComponent(email || '')}`, {}, { headers: authHeader() }),
    removeClientNotification: (clientId, notificationId) => axios.delete(ADMIN_API_URL + `client-notifications?clientId=${clientId}&notificationId=${notificationId}`, { headers: authHeader() })
}

export default adminService;
